import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faMobileAlt,
  faCreditCard,
  faWallet, // Added for balance display
  faMoneyBillTransfer // Added for withdrawal button
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard } from "../App";
import { Oval } from "react-loader-spinner";

const WithdrawalsPage = () => {
  const [balance, setBalance] = useState(null);
  const [showBalance, setShowBalance] = useState(true);
  const [withdrawalAmount, setWithdrawalAmount] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedChannel, setSelectedChannel] = useState(null); // 'mpesa' or 'airtelmoney'
  const [selectedMode, setSelectedMode] = useState(null); // 'mobilemoney' or 'cards'
  const [showMobileMoneyOptions, setShowMobileMoneyOptions] = useState(false);
  const [showCardPaymentOptions, setShowCardPaymentOptions] = useState(false);
  const [bankAccount, setBankAccount] = useState("");
  const [senderNames, setSenderNames] = useState("");
  const [bankCode, setBankCode] = useState("");
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState(""); // Stores selected bank code from dropdown
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  // Fetch token and user data once
  const token = localStorage.getItem("token");
  const userDataString = localStorage.getItem("user");
  const userData = userDataString ? JSON.parse(userDataString) : null;
  const userId = userData ? userData.unique : null;

  useEffect(() => {
    const fetchWalletData = async () => {
      if (!token || !userId) {
        toast.error("Authentication details missing. Please log in.");
        navigate("/login"); // Redirect if not authenticated
        return;
      }

      try {
        // Fetch user balance
        const balanceResponse = await axios.get(
          `https://backend.inyumba.co.ke/iNyumba/balance/user/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setBalance(
          balanceResponse.data.response.length > 0
            ? balanceResponse.data.response[0].balance
            : 0
        );

        // Fetch banks
        const banksResponse = await axios.get(
          "https://backend.inyumba.co.ke/iNyumba/admin/bank/fetch",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setBanks(banksResponse.data.response);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch wallet data or banks. Please try again.");
      }
    };

    fetchWalletData();
  }, [token, userId, navigate]); // Added navigate to dependencies

  const handleBankSelect = (e) => {
    const code = e.target.value;
    setBankCode(code);
    setSelectedBank(code);
  };

  const handleWithdrawalSubmit = async (e) => {
    e.preventDefault();

    if (!token || !userId) {
      toast.error("Authentication details missing. Please log in.");
      navigate("/login");
      return;
    }

    if (!withdrawalAmount || parseFloat(withdrawalAmount) <= 0) {
      toast.error("Please enter a valid withdrawal amount.");
      return;
    }

    if (balance !== null && parseFloat(withdrawalAmount) > balance) {
        toast.error("Insufficient funds in your account.");
        return;
    }

    setIsLoading(true);

    try {
      let response;
      let requestData;
      let url;

      if (selectedMode === "mobilemoney") {
        if (!phoneNumber || !selectedChannel) {
          toast.error("Please select a mobile money channel and enter phone number.");
          setIsLoading(false);
          return;
        }
        requestData = {
          amount: parseFloat(withdrawalAmount),
          phone_number: phoneNumber,
          channel: selectedChannel,
        };
        url = `https://backend.inyumba.co.ke/iNyumba/usersettlement/${userId}`;
      } else if (selectedMode === "cards") {
        if (!selectedBank || !bankAccount || !senderNames) {
          toast.error("Please fill all card payment details (Bank, Account, Sender Names).");
          setIsLoading(false);
          return;
        }
        requestData = {
          amount: parseFloat(withdrawalAmount),
          bank_account: bankAccount,
          bank_code: bankCode,
          sendernames: senderNames,
        };
        url = `https://backend.inyumba.co.ke/iNyumba/usersettlementcards/${userId}`;
      } else {
        toast.error("Please select a withdrawal mode (Mobile Money or Card Payments).");
        setIsLoading(false);
        return;
      }

      response = await axios.post(url, requestData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      // Handle responses
      const responseMessage = response.data.response;
      const responseCode = response.data.responseCode;

      if (responseCode === 200) {
        if (responseMessage === "B2C processing request") {
          toast.success("Withdrawal request submitted successfully! Funds will be processed.");
        } else if (responseMessage.includes("Insufficient funds") || responseMessage === "You have insufficient funds in your account. Transaction cost is 100") {
          toast.error(responseMessage);
        } else if (responseMessage === "B2C blocked request") {
          toast.error("Withdrawal request blocked. Please contact support.");
        } else {
          toast.info(responseMessage); // For other informational messages
        }
      } else {
        toast.error(responseMessage || "An error occurred during withdrawal.");
      }

      // Clear form fields on successful submission or specific responses
      setWithdrawalAmount("");
      setPhoneNumber("");
      setSelectedChannel(null);
      setSelectedMode(null);
      setShowMobileMoneyOptions(false);
      setShowCardPaymentOptions(false);
      setBankAccount("");
      setSenderNames("");
      setBankCode("");
      setSelectedBank("");

    } catch (error) {
      const errorMessage = error.response?.data?.response || "An unexpected error occurred. Please try again.";
      toast.error(errorMessage);
      console.error("Error submitting withdrawal:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleShowBalance = () => {
    setShowBalance(!showBalance);
  };

  const handleModeSelection = (mode) => {
    setSelectedMode(mode);
    setShowMobileMoneyOptions(mode === "mobilemoney");
    setShowCardPaymentOptions(mode === "cards");
    // Clear channel/bank selections when switching mode
    setSelectedChannel(null);
    setSelectedBank("");
    setBankCode("");
    setPhoneNumber("");
    setBankAccount("");
    setSenderNames("");
  };

  const handleMobileMoneyClick = (channel) => {
    setSelectedChannel(channel);
  };

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6 lg:p-8">
        
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-2xl overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-indigo-700 to-purple-800 p-6 text-white text-center sm:text-left">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
              <h1 className="text-4xl font-extrabold mb-2 sm:mb-0">Withdrawals 💸</h1>
              <span className="text-lg opacity-90">Date: {new Date().toLocaleDateString()}</span>
            </div>

            {/* Balance Display */}
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 flex items-center justify-between mt-4 shadow-inner">
              <p className="font-semibold text-lg flex items-center">
                <FontAwesomeIcon icon={faWallet} className="mr-3 text-yellow-300 text-2xl" />
                Your Balance:
              </p>
              <div className="flex items-center">
                <p className="mr-3 text-2xl font-bold text-yellow-300">
                  {balance !== null ? (showBalance ? `Ksh ${balance.toLocaleString()}` : "*******") : "Loading..."}
                </p>
                <FontAwesomeIcon
                  icon={showBalance ? faEyeSlash : faEye}
                  className="text-xl cursor-pointer text-white hover:text-yellow-300 transition-colors"
                  onClick={toggleShowBalance}
                  title={showBalance ? "Hide Balance" : "Show Balance"}
                />
              </div>
            </div>
          </div>

          {/* Withdrawal Form Section */}
          <div className="p-6 sm:p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-4">Withdrawal Options</h2>

            {/* Mode Selection (Mobile Money / Card Payments) */}
            <div className="flex flex-wrap gap-4 mb-6 justify-center">
              <button
                type="button"
                className={`flex items-center px-6 py-3 rounded-lg text-lg font-semibold transition-all duration-300 ease-in-out shadow-md
                  ${selectedMode === "mobilemoney"
                    ? "bg-blue-600 text-white transform scale-105"
                    : "bg-gray-200 text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                  }`}
                onClick={() => handleModeSelection("mobilemoney")}
              >
                <FontAwesomeIcon icon={faMobileAlt} className="mr-3 text-2xl" />
                Mobile Money
              </button>
              <button
                type="button"
                className={`flex items-center px-6 py-3 rounded-lg text-lg font-semibold transition-all duration-300 ease-in-out shadow-md
                  ${selectedMode === "cards"
                    ? "bg-blue-600 text-white transform scale-105"
                    : "bg-gray-200 text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                  }`}
                onClick={() => handleModeSelection("cards")}
              >
                <FontAwesomeIcon icon={faCreditCard} className="mr-3 text-2xl" />
                Card Payments
              </button>
            </div>

            {/* Dynamic Withdrawal Forms */}
            <form onSubmit={handleWithdrawalSubmit} className="space-y-6">
              <div className="mb-4">
                <label
                  htmlFor="withdrawalAmount"
                  className="block text-gray-700 font-semibold mb-2"
                >
                  Amount (Ksh)
                </label>
                <input
                  type="number"
                  id="withdrawalAmount"
                  value={withdrawalAmount}
                  placeholder="Enter amount to withdraw"
                  onChange={(e) => setWithdrawalAmount(e.target.value)}
                  className="block w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-lg"
                  min="1"
                  required
                />
              </div>

              {showMobileMoneyOptions && (
                <>
                  <div className="flex flex-wrap gap-4 mb-4 justify-center">
                    <button
                      type="button"
                      className={`flex items-center px-5 py-2 rounded-md font-medium transition-colors duration-300
                        ${selectedChannel === "mpesa"
                          ? "bg-green-600 text-white shadow-md"
                          : "bg-gray-200 text-gray-700 hover:bg-green-100 hover:text-green-800"
                        }`}
                      onClick={() => handleMobileMoneyClick("mpesa")}
                    >
                      <FontAwesomeIcon icon={faMobileAlt} className="mr-2" />
                      MPESA
                    </button>
                    <button
                      type="button"
                      className={`flex items-center px-5 py-2 rounded-md font-medium transition-colors duration-300
                        ${selectedChannel === "airtelmoney"
                          ? "bg-red-600 text-white shadow-md"
                          : "bg-gray-200 text-gray-700 hover:bg-red-100 hover:text-red-800"
                        }`}
                      onClick={() => handleMobileMoneyClick("airtelmoney")}
                    >
                      <FontAwesomeIcon icon={faMobileAlt} className="mr-2" />
                      Airtel Money
                    </button>
                  </div>

                  <div className="mb-4">
                    <label
                      htmlFor="phoneNumber"
                      className="block text-gray-700 font-semibold mb-2"
                    >
                      Phone Number
                    </label>
                    <p className="text-sm text-gray-500 mb-2">
                      Please enter in this format: e.g., 2547XXXXXXXX or 2541XXXXXXXX
                    </p>
                    <input
                      type="text"
                      id="phoneNumber"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="e.g., 254712345678"
                      className="block w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-lg"
                      required={selectedMode === "mobilemoney"}
                    />
                  </div>
                </>
              )}

              {showCardPaymentOptions && (
                <>
                  <div className="mb-4">
                    <label
                      htmlFor="bankCode"
                      className="block text-gray-700 font-semibold mb-2"
                    >
                      Select Bank
                    </label>
                    <select
                      id="bankCode"
                      value={selectedBank}
                      onChange={handleBankSelect}
                      className="block w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-lg bg-white"
                      required={selectedMode === "cards"}
                    >
                      <option value="">-- Select Bank --</option>
                      {banks.map((bank) => (
                        <option key={bank.id} value={bank.bankcode}>
                          {bank.bankname}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="senderNames"
                      className="block text-gray-700 font-semibold mb-2"
                    >
                      Account Holder Names
                    </label>
                    <input
                      type="text"
                      id="senderNames"
                      value={senderNames}
                      onChange={(e) => setSenderNames(e.target.value)}
                      className="block w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-lg"
                      placeholder="Full names as on bank account"
                      required={selectedMode === "cards"}
                    />
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="bankAccount"
                      className="block text-gray-700 font-semibold mb-2"
                    >
                      Bank Account Number
                    </label>
                    <input
                      type="text"
                      id="bankAccount"
                      value={bankAccount}
                      onChange={(e) => setBankAccount(e.target.value)}
                      className="block w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-lg"
                      placeholder="Enter bank account number"
                      required={selectedMode === "cards"}
                    />
                  </div>
                </>
              )}

              <button
                type="submit"
                className={`w-full py-3 px-6 rounded-lg text-lg font-semibold transition-all duration-300 ease-in-out flex items-center justify-center space-x-3
                  ${isLoading || !selectedMode
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-indigo-700 text-white hover:bg-indigo-800 transform hover:scale-[1.01]"
                  }`}
                disabled={isLoading || !selectedMode}
              >
                {isLoading ? (
                  <>
                    <Oval
                      height={20}
                      width={20}
                      color="white"
                      ariaLabel="loading-spinner"
                    />
                    <span>Processing Withdrawal...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faMoneyBillTransfer} className="text-xl" />
                    <span>Submit Withdrawal</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </AuthGuard>
  );
};

export default WithdrawalsPage;